"use client";
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  ArrowLeft, Upload, Image as ImageIcon, Video, Trash2,
  Plus, Save, X, Package, Tag, DollarSign, Layers,
  Palette, Ruler, Lock,
  IndianRupee,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useProducts }         from '../context/ProductsContext';
import { useCategories }       from '../context/CategoryContext';
import { useSubCategories }    from '../context/SubcategoryContext';
import { useModels }           from '../context/ModelContext';
import { useColors }           from '../context/ColorContext';
import { useProductTypes }     from '../context/ProductTypeContext.jsx';
import { useProductMaterials } from '../context/ProductMaterialContext';
import { useProductShapes }    from '../context/ProductShapeContext.jsx';
import { useCountries }        from '../context/Countrycontext';
import { CDN_URL }             from '../config/apiconfig.jsx';

// ── Constants ─────────────────────────────────────────────────────────────────
const MAX_PRODUCT_DETAILS = 5;

const CAPITALIZE_FIELDS = new Set([
  'product_name', 'brand_name', 'model_name', 'manufacturer',
  'item_type_name', 'style', 'frame_color', 'frame_material',
  'frame_type', 'size', 'unit_count_type',
]);

const OPTIONAL_FIELDS = [
  'product_type', 'brand_name', 'sku', 'model_name', 'manufacturer',
  'item_type_name', 'material', 'color', 'size', 'item_shape', 'style', 'no_of_items',
  'frame_color', 'frame_material', 'frame_type',
  'max_retail_price', 'sale_price', 'min_seller_allowed_price', 'max_seller_allowed_price',
  'prod_tax_code', 'sale_start_date', 'sale_end_date', 'offering_relase_date',
  'merchant_release_date', 'max_order_quantity', 'min_order_qty', 'offering_message',
  'item_length_longer_edge', 'item_length_unit', 'item_width_shorter_edge', 'item_width_unit',
  'item_depth', 'item_weight', 'item_weight_unit', 'unit_count', 'unit_count_type',
  'item_package_length', 'package_length_unit', 'item_package_width', 'package_width_unit',
  'item_package_height', 'package_height_unit', 'item_package_weight', 'package_weight_unit','return_policy',
];

const INITIAL_FORM = {
  product_type: '', product_name: '', brand_name: '',
  model_name: '', manufacturer: '',
  style: '', material: '', no_of_items: '1', item_type_name: '',
  color: '', size: '', item_shape: '',
  frame_color: '', frame_material: '', frame_type: '',
  item_depth: '', unit_count: '', unit_count_type: '',
  item_length_longer_edge: '', item_length_unit: 'cm',
  item_width_shorter_edge: '', item_width_unit: 'cm',
  sku: '', quantity: '', your_price: '',
  max_retail_price: '', min_seller_allowed_price: '',
  max_seller_allowed_price: '', sale_price: '',
  sale_start_date: '', sale_end_date: '',
  offering_relase_date: '', prod_tax_code: '',
  merchant_release_date: '', max_order_quantity: '',
  min_order_qty: '1',
  offering_message: '', wrap_available: false,
  item_weight: '', item_weight_unit: 'kg',
  item_package_length: '', package_length_unit: 'cm',
  item_package_width: '',  package_width_unit: 'cm',
  item_package_height: '', package_height_unit: 'cm',
  item_package_weight: '', package_weight_unit: 'kg',
  categoryId: '', subCategoryId: '', modalCategoryId: '',
  status: 'Active',
  images: [],
  video: '',
  productDetails: [],
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const blankDetail = () => ({ title: '', content: '', image: null, preview: null });

const parseProductDetails = (description, baseUrl) => {
  if (!description) return [];
  try {
    const parsed = JSON.parse(description);
    if (Array.isArray(parsed)) {
      return parsed.map(d => ({
        title:   d.title   || '',
        content: d.content || '',
        image:   null,
        preview: d.image
          ? d.image.startsWith('http')
            ? d.image
            : `${baseUrl}/${d.image}`
          : null,
      }));
    }
  } catch { /* plain-text description */ }
  return [];
};

// Helper functions for numeric input handling
const handleNumericWheel = (e) => {
  e.target.blur();
};

const handleNumericKeyDown = (e) => {
  // Prevent negative values
  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
    e.preventDefault();
  }
};

const preventNegativeInput = (value) => {
  const numValue = parseFloat(value);
  return isNaN(numValue) || numValue < 0 ? '' : value;
};

// ── Component ─────────────────────────────────────────────────────────────────
const AddProductPage = () => {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const isEditMode = Boolean(id);

 const { addProduct, updateProduct, products, returnPolicies } = useProducts();
  const { categories = [],    loading: categoriesLoading    } = useCategories();
  const { subCategories = [], loading: subCategoriesLoading } = useSubCategories();
  const { models }                                            = useModels();
  const { colors,    loading: colorsLoading  }               = useColors();
  const { productTypes,   producttypeloading }               = useProductTypes();
  const { productMaterials, materialloading  }               = useProductMaterials();
  const { shapes }                                           = useProductShapes();

  const [formData,       setFormData]       = useState(INITIAL_FORM);
  const [errors,         setErrors]         = useState({});
  const [isLoading,      setIsLoading]      = useState(false);
  const [isDragging,     setIsDragging]     = useState(false);
  const [productLoading, setProductLoading] = useState(false);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // ── Edit-mode: populate form ────────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !id || products.length === 0) return;

    setProductLoading(true);
   const product = products.find(p => String(p.id) === String(id));
console.log('productDetails:', JSON.stringify(product?.productDetails, null, 1));
console.log('description:', product?.description);

    if (!product) {
      navigate('/products');
      setProductLoading(false);
      return;
    }

   const resolvedDetails =
  Array.isArray(product.productDetails) && product.productDetails.length > 0
    ? product.productDetails.map(d => ({
        title:   d.title   || '',
        content: d.content || '',
        image:   null,
        // Handles full URLs, relative paths, or already-set previews
        preview: d.preview
          ? d.preview
          : d.image
            ? d.image.startsWith('http')
              ? d.image
              : `${CDN_URL}/${d.image}`
            : null,
      }))
    : parseProductDetails(product.description, CDN_URL);

    setFormData(prev => ({
      ...prev,
      product_name:             product.name                    || '',
      brand_name:               product.brand_name              || '',
      model_name:               product.model_name              || '',
      manufacturer:             product.manufacturer            || '',
      item_type_name:           product.item_type_name          || '',
      style:                    product.style                   || '',
      product_type:             product.product_type            || '',
      color:                    product.color                   || '',
      material:                 product.material                || '',
      size:                     product.size                    || '',
      item_shape:               product.item_shape              || '',
      frame_color:              product.frame_color             || '',
      frame_material:           product.frame_material          || '',
      frame_type:               product.frame_type              || '',
      your_price:               product.your_price              || '',
      max_retail_price:         product.max_retail_price        || '',
      min_seller_allowed_price: product.min_seller_allowed_price|| '',
      max_seller_allowed_price: product.max_seller_allowed_price|| '',
      sale_price: (!product.your_price || parseFloat(product.your_price) === 0)
        ? (product.max_retail_price || '')
        : (product.sale_price || ''),
      prod_tax_code:            product.prod_tax_code           || '',
      sale_start_date:          product.sale_start_date         || '',
      sale_end_date:            product.sale_end_date           || '',
      offering_relase_date:     product.offering_relase_date    || '',
      merchant_release_date:    product.merchant_release_date   || '',
      max_order_quantity:       product.max_order_quantity      || '',
      min_order_qty:            product.min_order_qty           || '1',
      offering_message:         product.offering_message        || '',
      wrap_available:           !!product.wrap_available,
      sku:                      product.sku                     || '',
      quantity:                 product.stock                   || '',
      status:                   product.status                  || 'Active',
      item_length_longer_edge:  product.item_length_longer_edge || '',
      item_length_unit:         product.item_length_unit        || 'cm',
      item_width_shorter_edge:  product.item_width_shorter_edge || '',
      item_width_unit:          product.item_width_unit         || 'cm',
      item_depth:               product.item_depth              || '',
      item_weight:              product.item_weight             || '',
      item_weight_unit:         product.item_weight_unit        || 'kg',
      unit_count:               product.unit_count              || '',
      unit_count_type:          product.unit_count_type         || '',
      item_package_length:      product.item_package_length     || '',
      package_length_unit:      product.package_length_unit     || 'cm',
      item_package_width:       product.item_package_width      || '',
      package_width_unit:       product.package_width_unit      || 'cm',
      item_package_height:      product.item_package_height     || '',
      package_height_unit:      product.package_height_unit     || 'cm',
      item_package_weight:      product.item_package_weight     || '',
      package_weight_unit:      product.package_weight_unit     || 'kg',
      categoryId:               product.categoryId              || '',
      subCategoryId:            product.subCategoryId           || '',
      return_policy: product.return_policy || '',
      modalCategoryId:          product.modalCategoryId         || '',
      images:       (product.images || []).map(url => ({ file: null, preview: url })),
      video:        product.video    || '',
      productDetails: resolvedDetails,
    }));

    setProductLoading(false);
  }, [isEditMode, id, products, navigate]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const filteredSubCategories = subCategories.filter(
    s => s.category_id === formData.categoryId
  );

  const filteredModalCategories = useMemo(() => {
    if (!Array.isArray(models) || !formData.subCategoryId) return [];
    return models.filter(
      m => String(m.sub_category_id) === String(formData.subCategoryId) && m.status === 'Active'
    );
  }, [models, formData.subCategoryId]);

  const hasModalCategories = filteredModalCategories.length > 0;
  const detailCount        = formData.productDetails.length;
  const isDetailLimitHit   = detailCount >= MAX_PRODUCT_DETAILS;

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleInputChange = ({ target: { name, value, type, checked } }) => {
    let cooked = value;

    if (type === 'checkbox') {
      cooked = checked;
    } else if (type === 'number') {
      // Prevent negative values for numeric inputs
      cooked = preventNegativeInput(value);
      if (cooked === '') {
        cooked = '';
      } else {
        cooked = parseFloat(cooked);
      }
    } else if (CAPITALIZE_FIELDS.has(name) && typeof value === 'string') {
      cooked = value.charAt(0).toUpperCase() + value.slice(1);
    }

    setFormData(prev => {
      const next = { ...prev, [name]: cooked };

      if (name === 'your_price') {
        const n = parseFloat(cooked);
        next.sale_price = (!cooked || n === 0) ? (prev.max_retail_price || '') : cooked;
      }
      if (name === 'max_retail_price' && (!prev.your_price || parseFloat(prev.your_price) === 0)) {
        next.sale_price = cooked;
      }
      if (name === 'categoryId') {
        next.subCategoryId   = '';
        next.modalCategoryId = '';
      }
      if (name === 'subCategoryId') {
        next.modalCategoryId = '';
      }
      if (name === 'item_length_unit') {
        next.item_width_unit     = cooked;
        next.package_length_unit = cooked;
        next.package_width_unit  = cooked;
        next.package_height_unit = cooked;
      }
      if (name === 'item_weight_unit') {
        next.package_weight_unit = cooked;
      }

      return next;
    });

    // Clear field-level error as user types
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Product Details
  const addProductDetail = () => {
    if (isDetailLimitHit) return;
    setFormData(prev => ({ ...prev, productDetails: [...prev.productDetails, blankDetail()] }));
  };

  const removeProductDetail = idx =>
    setFormData(prev => {
      const updated = [...prev.productDetails];
      if (updated[idx]?.image instanceof File) URL.revokeObjectURL(updated[idx].preview);
      return { ...prev, productDetails: updated.filter((_, i) => i !== idx) };
    });

  const updateDetailField = (idx, field, value) =>
    setFormData(prev => {
      const updated = [...prev.productDetails];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, productDetails: updated };
    });

  const handleDetailImageUpload = (idx, file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setFormData(prev => {
      const updated = [...prev.productDetails];
      if (updated[idx]?.image instanceof File) URL.revokeObjectURL(updated[idx].preview);
      updated[idx] = { ...updated[idx], image: file, preview };
      return { ...prev, productDetails: updated };
    });
  };

  const removeDetailImage = idx =>
    setFormData(prev => {
      const updated = [...prev.productDetails];
      if (updated[idx]?.image instanceof File) URL.revokeObjectURL(updated[idx].preview);
      updated[idx] = { ...updated[idx], image: null, preview: null };
      return { ...prev, productDetails: updated };
    });

  // Images / Video
  const handleImageUpload = files => {
    const selected = Array.from(files).slice(0, 5 - formData.images.length);
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...selected.map(file => ({ file, preview: URL.createObjectURL(file) }))],
    }));
  };

  const handleVideoUpload = file => {
    if (!file) return;
    if (file.type !== 'video/mp4') {
      setErrors(prev => ({ ...prev, video: 'Only MP4 videos are allowed' }));
      return;
    }
    setErrors(prev => ({ ...prev, video: '' }));
    setFormData(prev => ({ ...prev, video: file }));
  };

  const removeImage = idx => {
    const removed = formData.images[idx];
    if (removed?.file) URL.revokeObjectURL(removed.preview);
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const removeVideo = () => setFormData(prev => ({ ...prev, video: '' }));

  const handleDragOver  = e => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = e => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const imgs  = files.filter(f => f.type.startsWith('image/'));
    const vids  = files.filter(f => f.type.startsWith('video/'));
    if (imgs.length) handleImageUpload(imgs);
    if (vids.length && !formData.video) handleVideoUpload(vids[0]);
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateForm = () => {
    const e = {};
    if (!formData.product_name.trim()) e.product_name  = 'Product name is required';
    if (!formData.categoryId)          e.categoryId    = 'Category is required';
    if (!formData.subCategoryId)       e.subCategoryId = 'Sub-category is required';
    if (hasModalCategories && !formData.modalCategoryId)
      e.modalCategoryId = 'Modal category is required for this sub-category';
    if (formData.your_price === '' || formData.your_price === null)
      e.your_price = 'Valid price is required';

    if (!isEditMode && !formData.images.some(img => img?.file instanceof File))
      e.images = 'At least one image is required';

    // ── min_order_qty validation ────────────────────────────────────────────
    const minOrd = parseFloat(formData.min_order_qty);
    const maxOrd = parseFloat(formData.max_order_quantity);

    if (formData.min_order_qty === '' || isNaN(minOrd)) {
      e.min_order_qty = 'Minimum order quantity is required';
    } else if (minOrd < 1) {
      e.min_order_qty = 'Minimum order quantity must be at least 1';
    }

    // ── max_order_quantity must be greater than min_order_qty ──────────────
    if (formData.max_order_quantity !== '' && !isNaN(maxOrd)) {
      if (maxOrd <= minOrd) {
        e.max_order_quantity = `Max order quantity (${maxOrd}) must be greater than min order quantity (${minOrd})`;
      }
    }

    const dimChecks = [
      ['item_package_length', 'item_length_longer_edge', 'item_length_unit', 'Package length must be ≥ product length'],
      ['item_package_width',  'item_width_shorter_edge',  'item_width_unit',  'Package width must be ≥ product width'],
      ['item_package_height', 'item_depth',               'item_length_unit', 'Package height must be ≥ product depth'],
      ['item_package_weight', 'item_weight',              'item_weight_unit', 'Package weight must be ≥ product weight'],
    ];
    dimChecks.forEach(([pkgF, prodF, unitF, msg]) => {
      const pkg  = parseFloat(formData[pkgF]);
      const prod = parseFloat(formData[prodF]);
      if (!isNaN(pkg) && !isNaN(prod) && pkg < prod)
        e[pkgF] = `${msg} (${prod} ${formData[unitF]})`;
    });

    // ── Product detail image validation ──────────────────────────────────
  formData.productDetails.forEach((detail, idx) => {
  if (!detail.image && !detail.preview) {
    e[`detail_image_${idx}`] = 'Image is required for this detail';
  }
});

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Reset ───────────────────────────────────────────────────────────────────
  const resetForm = () => setFormData(INITIAL_FORM);

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const fd = new FormData();

      const yourPriceIsZero =
        !formData.your_price || parseFloat(formData.your_price) === 0;
      const effectiveYourPrice = yourPriceIsZero
        ? (formData.max_retail_price || '0')
        : formData.your_price;
      const effectiveSalePrice = yourPriceIsZero
        ? (formData.max_retail_price || '0')
        : formData.sale_price;

      fd.append('categoryId',    formData.categoryId);
      fd.append('subCategoryId', formData.subCategoryId);
      fd.append('product_name',  formData.product_name);
      fd.append('your_price',    effectiveYourPrice);
      fd.append('quantity', formData.quantity !== '' && formData.quantity != null ? formData.quantity : 0);
      fd.append('status',        formData.status || 'Active');
      fd.append('min_order_qty', formData.min_order_qty);

      if (formData.modalCategoryId) fd.append('modalCategoryId', formData.modalCategoryId);

      OPTIONAL_FIELDS.forEach(field => {
        if (field === 'sale_price') {
          if (effectiveSalePrice !== '' && effectiveSalePrice != null)
            fd.append('sale_price', effectiveSalePrice);
          return;
        }
        if (formData[field] !== '' && formData[field] != null)
          fd.append(field, formData[field]);
      });

      fd.append('wrap_available', formData.wrap_available ? '1' : '0');

      formData.images
        .filter(img => img?.file instanceof File)
        .forEach(img => fd.append('images[]', img.file));

      if (formData.video instanceof File) fd.append('video', formData.video);

    formData.productDetails.slice(0, MAX_PRODUCT_DETAILS).forEach((detail, idx) => {
  fd.append(`description[${idx}][title]`,   detail.title);
  fd.append(`description[${idx}][content]`, detail.content);
  if (detail.image instanceof File) {
    // New file selected — send the file
    fd.append(`description[${idx}][image]`, detail.image);
  } else if (detail.preview) {
    // Existing image — send the existing URL/path so backend keeps it
    fd.append(`description[${idx}][existing_image]`, detail.preview);
  }
});

      if (isEditMode) fd.append('_method', 'POST');

      const result = isEditMode ? await updateProduct(id, fd) : await addProduct(fd);

      if (result?.success && [200, 201].includes(result?.status)) {
        resetForm();
        navigate('/products');
        return;
      }

      setErrors({ general: result?.message || 'Operation failed' });
    } catch (err) {
      console.error('Submit error:', err);
      setErrors({ general: 'Failed to save product' });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Loading state ───────────────────────────────────────────────────────────
  if (categoriesLoading || subCategoriesLoading || productLoading) {
    return (
      <div className="min-h-screen bg-orange-50 bg-opacity-90 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  // ── Shared input class helpers ──────────────────────────────────────────────
  const inputCls = (err) =>
    `w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 ${err ? 'border-red-300' : 'border-orange-200'}`;

  const numericInputCls = (err) =>
    `w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 ${err ? 'border-red-300' : 'border-orange-200'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;

  const selectCls = 'w-full px-4 py-3 bg-white border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400';

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-orange-50 bg-opacity-90">
      <style jsx>{`
        /* Hide number input spinners */
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        input[type="number"] {
          -moz-appearance: textfield;
        }
      `}</style>

      {/* Header */}
      <div className="bg-white bg-opacity-80 backdrop-blur-lg border-b border-orange-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/products" className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors">
              <ArrowLeft className="w-5 h-5" /><span>Back to Products</span>
            </Link>
            <div className="w-px h-6 bg-gray-300" />
            <div>
              <h1 className="text-2xl font-bold text-teal-800">
                {isEditMode ? 'Edit Product' : 'Add New Product'}
              </h1>
              <p className="text-gray-600 text-sm">
                {isEditMode ? 'Update product information' : 'Create a comprehensive product listing'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate('/products')} disabled={isLoading}
              className="px-6 py-2 bg-white bg-opacity-80 text-gray-700 rounded-lg hover:bg-opacity-100 border border-gray-300 disabled:opacity-50 transition-all">
              Cancel
            </button>
            <button type="submit" form="add-product-form" disabled={isLoading}
              className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-all">
              {isLoading
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{isEditMode ? 'Updating…' : 'Saving…'}</>
                : <><Save className="w-4 h-4" />{isEditMode ? 'Update Product' : 'Save Product'}</>}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <form id="add-product-form" onSubmit={handleSubmit} className="space-y-8">

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {errors.general}
            </div>
          )}

          {/* Categories */}
          <Section icon={<Tag className="w-6 h-6 text-teal-600" />} title="Categories">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label required>Category</Label>
                <select name="categoryId" value={formData.categoryId} onChange={handleInputChange} className={inputCls(errors.categoryId)}>
                  <option value="">Select a category…</option>
                  {categories.filter(c => ['Active', 1, '1'].includes(c.status)).map(c =>
                    <option key={c.id} value={c.id}>{c.name}</option>
                  )}
                </select>
                <FieldError msg={errors.categoryId} />
              </div>
              <div>
                <Label required>Sub-Category</Label>
                <select name="subCategoryId" value={formData.subCategoryId} onChange={handleInputChange}
                  disabled={!formData.categoryId} className={inputCls(errors.subCategoryId)}>
                  <option value="">Select a sub-category…</option>
                  {filteredSubCategories.map(s => <option key={s.id} value={s.id}>{s.subcat_name}</option>)}
                </select>
                <FieldError msg={errors.subCategoryId} />
              </div>
              <div>
                <Label required={hasModalCategories}>Modal Category</Label>
                <select name="modalCategoryId" value={formData.modalCategoryId} onChange={handleInputChange}
                  disabled={!formData.subCategoryId || !filteredModalCategories.length}
                  className={`${inputCls(errors.modalCategoryId)} ${!filteredModalCategories.length ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <option value="">{filteredModalCategories.length ? 'Select a model…' : 'No models available'}</option>
                  {filteredModalCategories.map(m => <option key={m.id} value={m.id}>{m.model_name}</option>)}
                </select>
                <FieldError msg={errors.modalCategoryId} />
              </div>
            </div>
          </Section>

          {/* Basic Information */}
          <Section icon={<Package className="w-6 h-6 text-teal-600" />} title="Basic Information">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-2">
                <Label required>Product Name</Label>
                <input type="text" name="product_name" value={formData.product_name} onChange={handleInputChange}
                  placeholder="Enter a descriptive product name…" className={inputCls(errors.product_name)} />
                <FieldError msg={errors.product_name} />
              </div>
              {[
                ['product_type', 'Product Type', 'select'],
                ['brand_name',   'Brand Name',   'text',   'Enter brand name'],
                ['sku',          'SKU',          'text',   'e.g., PROD-001'],
                ['model_name',   'Model Name',   'text',   'Enter model name'],
                ['manufacturer', 'Manufacturer', 'text',   'Enter manufacturer name'],
                ['item_type_name','Item Type',   'text',   'e.g., Wall Art, Poster, Canvas'],
              ].map(([name, label, type, ph]) => (
                <div key={name}>
                  <Label>{label}</Label>
                  {type === 'select' ? (
                    <select name={name} value={formData[name]} onChange={handleInputChange} className={selectCls}>
                      <option value="">{producttypeloading ? 'Loading…' : 'Select product type'}</option>
                      {productTypes?.filter(p => p.status === 'Active').map(t =>
                        <option key={t.id} value={t.id}>{t.type_name}</option>
                      )}
                    </select>
                  ) : (
                    <input type="text" name={name} value={formData[name]} onChange={handleInputChange}
                      placeholder={ph} className={inputCls()} />
                  )}
                </div>
              ))}
            </div>
          </Section>

          {/* Images & Videos */}
          <Section icon={<ImageIcon className="w-6 h-6 text-teal-600" />} title="Images & Videos">
            <div className="mb-8">
              <Label required={!isEditMode}>Product Images (Max 5)</Label>
              {formData.images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
                  {formData.images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img.preview || img} alt={`Product ${i + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-orange-200" />
                      <button type="button" onClick={() => removeImage(i)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                      {i === 0 && (
                        <div className="absolute bottom-2 left-2 px-2 py-1 bg-teal-600 text-white text-xs rounded">Main</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {formData.images.length < 5 && (
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                    ${isDragging ? 'border-teal-400 bg-teal-50' : 'border-orange-300 hover:border-teal-300'}
                    ${errors.images ? 'border-red-300' : ''}`}
                  onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                  <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-3">Drag and drop images here, or click to browse</p>
                  <button type="button" onClick={() => imageInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors">
                    <Upload className="w-5 h-5" /> Choose Images
                  </button>
                  <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={e => handleImageUpload(e.target.files)} />
                  <p className="text-xs text-gray-500 mt-3">PNG, JPG up to 5 MB each. First image is the main image.</p>
                </div>
              )}
              <FieldError msg={errors.images} />
            </div>

            <div>
              <Label>Product Video (Optional)</Label>
              {formData.video ? (
                <div className="relative">
                  <video
                    src={typeof formData.video === 'string' ? formData.video : URL.createObjectURL(formData.video)}
                    className="w-full h-64 object-cover rounded-lg border border-orange-200" controls />
                  <button type="button" onClick={removeVideo}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-orange-300 rounded-xl p-8 text-center">
                  <Video className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-3">Upload a product demonstration video</p>
                  <button type="button" onClick={() => videoInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700">
                    <Upload className="w-5 h-5" /> Choose Video
                  </button>
                  <input ref={videoInputRef} type="file" accept="video/*" className="hidden"
                    onChange={e => handleVideoUpload(e.target.files[0])} />
                  <p className="text-xs text-gray-500 mt-3">MP4 format, up to 50 MB</p>
                </div>
              )}
              <FieldError msg={errors.video} />
            </div>
          </Section>

          {/* Product Attributes */}
          <Section icon={<Palette className="w-6 h-6 text-teal-600" />} title="Product Attributes">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <Label>Material</Label>
                <select name="material" value={formData.material} onChange={handleInputChange} className={selectCls}>
                  <option value="">{materialloading ? 'Loading…' : 'Select material'}</option>
                  {productMaterials?.filter(m => m.status === 'Active').map(m =>
                    <option key={m.id} value={m.id}>{m.name}</option>
                  )}
                </select>
              </div>
              <div>
                <Label>Color</Label>
                <select name="color" value={formData.color} onChange={handleInputChange}
                  disabled={colorsLoading} className={selectCls}>
                  <option value="">{colorsLoading ? 'Loading…' : 'Select color'}</option>
                  {colors?.filter(c => c.status === 'Active').map(c =>
                    <option key={c.id} value={c.id}>{c.color_name}</option>
                  )}
                </select>
              </div>
              <div>
                <Label>Size</Label>
                <input type="text" name="size" value={formData.size} onChange={handleInputChange}
                  placeholder="e.g., 12×16 inches, Large" className={inputCls()} />
              </div>
              <div>
                <Label>Shape</Label>
                <select name="item_shape" value={formData.item_shape} onChange={handleInputChange} className={selectCls}>
                  <option value="">Select shape</option>
                  {shapes?.filter(s => s.status === 'Active').map(s =>
                    <option key={s.id} value={s.id}>{s.name}</option>
                  )}
                </select>
              </div>
              <div>
                <Label>Style</Label>
                <input type="text" name="style" value={formData.style} onChange={handleInputChange}
                  placeholder="e.g., Modern, Vintage, Abstract" className={inputCls()} />
              </div>
              <div>
                <Label>Number of Items</Label>
                <input type="number" name="no_of_items" value={formData.no_of_items} onChange={handleInputChange}
                  placeholder="1" min="0" className={numericInputCls()}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
              </div>
            </div>
          </Section>

          {/* Frame & Art Specific */}
          <Section icon={<Layers className="w-6 h-6 text-teal-600" />} title="Frame & Art Specific">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                ['frame_color',    'Frame Color',    'e.g., Black, White, Gold'],
                ['frame_material', 'Frame Material', 'e.g., Wood, Metal'],
                ['frame_type',     'Frame Type',     'e.g., Standard, Gallery'],
              ].map(([name, label, ph]) => (
                <div key={name}>
                  <Label>{label}</Label>
                  <input type="text" name={name} value={formData[name]} onChange={handleInputChange}
                    placeholder={ph} className={inputCls()} />
                </div>
              ))}
            </div>
          </Section>

          {/* Pricing & Inventory */}
          <Section icon={<IndianRupee className="w-6 h-6 text-teal-600" />} title="Pricing & Inventory">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <Label required>Your Price (₹)</Label>
                <input type="number" name="your_price" value={formData.your_price} onChange={handleInputChange}
                  placeholder="0.00" min="0" step="0.01" className={numericInputCls(errors.your_price)}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                <FieldError msg={errors.your_price} />
              </div>
              {[
                ['max_retail_price',         'MRP (₹)'],
                ['sale_price',               'Sale Price (₹)'],
                ['min_seller_allowed_price', 'Min Seller Price (₹)'],
                ['max_seller_allowed_price', 'Max Seller Price (₹)'],
              ].map(([name, label]) => (
                <div key={name}>
                  <Label>{label}</Label>
                  <input type="number" name={name} value={formData[name]} onChange={handleInputChange}
                    placeholder="0.00" min="0" step="0.01" className={numericInputCls()}
                    onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                </div>
              ))}
              <div>
                <Label>Product Tax Code</Label>
                <input type="text" name="prod_tax_code" value={formData.prod_tax_code} onChange={handleInputChange}
                  placeholder="Tax classification code" className={inputCls()} />
              </div>
              {[
                ['sale_start_date',       'Sale Start Date'],
                ['sale_end_date',         'Sale End Date'],
                ['offering_relase_date',  'Offering Release Date'],
                ['merchant_release_date', 'Merchant Release Date'],
              ].map(([name, label]) => (
                <div key={name}>
                  <Label>{label}</Label>
                  <input type="date" name={name} value={formData[name]} onChange={handleInputChange} className={inputCls()} />
                </div>
              ))}
              <div>
                <Label>Stock Quantity</Label>
                <input type="number" name="quantity" value={formData.quantity} onChange={handleInputChange}
                  placeholder="0" min="0" className={numericInputCls()}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                <FieldError msg={errors.quantity} />
              </div>
              <div>
                <Label>Return Policy</Label>
                <select
                  name="return_policy"
                  value={formData.return_policy}
                  onChange={handleInputChange}
                  className={selectCls}
                >
                  <option value="">Select return policy</option>
                  {returnPolicies?.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.policy_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* ── Min Order Quantity ── */}
              <div>
                <Label required>Min Order Quantity</Label>
                <input
                  type="number"
                  name="min_order_qty"
                  value={formData.min_order_qty}
                  onChange={handleInputChange}
                  placeholder="1"
                  min="0"
                  className={numericInputCls(errors.min_order_qty)}
                  onWheel={handleNumericWheel}
                  onKeyDown={handleNumericKeyDown}
                />
                <FieldError msg={errors.min_order_qty} />
              </div>

              {/* ── Max Order Quantity ── */}
              <div>
                <Label>Max Order Quantity</Label>
                <input
                  type="number"
                  name="max_order_quantity"
                  value={formData.max_order_quantity}
                  onChange={handleInputChange}
                  placeholder="No limit"
                  min="0"
                  className={numericInputCls(errors.max_order_quantity)}
                  onWheel={handleNumericWheel}
                  onKeyDown={handleNumericKeyDown}
                />
                {formData.min_order_qty !== '' && !errors.max_order_quantity && (
                  <p className="text-xs text-gray-400 mt-1">
                    Must be greater than min order quantity ({formData.min_order_qty})
                  </p>
                )}
                <FieldError msg={errors.max_order_quantity} />
              </div>

              <div>
                <Label>Product Status</Label>
                <div className="flex gap-4">
                  {['Active', 'Inactive'].map(val => (
                    <label key={val} className="flex items-center cursor-pointer">
                      <input type="radio" name="status" value={val} checked={formData.status === val}
                        onChange={handleInputChange} className="sr-only" />
                      <div className={`relative w-6 h-6 rounded-full border-2 transition-all
                        ${formData.status === val
                          ? val === 'Active' ? 'border-green-500 bg-green-500' : 'border-gray-500 bg-gray-500'
                          : 'border-gray-300 bg-white'}`}>
                        {formData.status === val && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full" />
                          </div>
                        )}
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-700">{val}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-3">
                <Label>Offering Message</Label>
                <textarea name="offering_message" value={formData.offering_message} onChange={handleInputChange}
                  placeholder="Special offering or promotional message…" rows={3}
                  className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none" />
              </div>
              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input type="checkbox" name="wrap_available" checked={formData.wrap_available}
                    onChange={handleInputChange} className="sr-only" />
                  <div className={`relative w-6 h-6 rounded border-2 transition-all
                    ${formData.wrap_available ? 'border-teal-500 bg-teal-500' : 'border-gray-300 bg-white'}`}>
                    {formData.wrap_available && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-sm" />
                      </div>
                    )}
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700">Gift Wrap Available</span>
                </label>
              </div>
            </div>
          </Section>

          {/* Physical Dimensions */}
          <Section icon={<Ruler className="w-6 h-6 text-teal-600" />} title="Physical Dimensions">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Product Dimensions */}
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Product Dimensions</h3>
                <div className="space-y-4">
                  <DimRow label="Length (Longer Edge)" valName="item_length_longer_edge" unitName="item_length_unit"
                    formData={formData} onChange={handleInputChange} />
                  <DimRow label="Width (Shorter Edge)" valName="item_width_shorter_edge" unitName="item_width_unit"
                    formData={formData} onChange={handleInputChange} units={['cm','inches','mm']} />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Depth / Thickness</label>
                      <input type="number" name="item_depth" value={formData.item_depth} onChange={handleInputChange}
                        placeholder="0.0" min="0" step="0.1"
                        className="w-full px-4 py-2 bg-white border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Weight</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="number" name="item_weight" value={formData.item_weight} onChange={handleInputChange}
                          placeholder="0.0" min="0" step="0.01"
                          className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                        <select name="item_weight_unit" value={formData.item_weight_unit} onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400">
                          {['kg','g','lbs'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Unit Count</label>
                      <input type="number" name="unit_count" value={formData.unit_count} onChange={handleInputChange}
                        placeholder="1" min="0"
                        className="w-full px-4 py-2 bg-white border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Unit Count Type</label>
                      <input type="text" name="unit_count_type" value={formData.unit_count_type} onChange={handleInputChange}
                        placeholder="pieces, sets…"
                        className="w-full px-4 py-2 bg-white border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Package Dimensions */}
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Package Dimensions</h3>
                <div className="space-y-4">
                  {[
                    ['item_package_length', 'package_length_unit', 'Package Length'],
                    ['item_package_width',  'package_width_unit',  'Package Width'],
                    ['item_package_height', 'package_height_unit', 'Package Height'],
                  ].map(([valName, unitName, label]) => (
                    <DimRow key={valName} label={label} valName={valName} unitName={unitName}
                      formData={formData} onChange={handleInputChange} error={errors[valName]} />
                  ))}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Package Weight</label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <input type="number" name="item_package_weight" value={formData.item_package_weight} onChange={handleInputChange}
                          placeholder="0.0" min="0" step="0.01"
                          className="w-full px-4 py-2 bg-white border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                        <FieldError msg={errors.item_package_weight} />
                      </div>
                      <select name="package_weight_unit" value={formData.package_weight_unit} onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-white border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400">
                        {['kg','g','lbs'].map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* Product Details */}
          <Section
            icon={<ImageIcon className="w-6 h-6 text-teal-600" />}
            title="Product Details"
            headerRight={
              <div className="relative group">
                <button type="button" onClick={addProductDetail} disabled={isDetailLimitHit}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${isDetailLimitHit
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                      : 'bg-teal-600 text-white hover:bg-teal-700'}`}>
                  {isDetailLimitHit ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  Add Detail
                </button>
                {isDetailLimitHit && (
                  <div className="absolute right-0 top-full mt-2 z-10 w-56 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity shadow-lg">
                    <Lock className="w-3 h-3 inline mr-1" />
                    Maximum of {MAX_PRODUCT_DETAILS} product details allowed. Remove one to add another.
                  </div>
                )}
              </div>
            }
          >
            <div className="flex items-center justify-between -mt-2 mb-4">
              <p className="text-sm text-gray-500">
                {detailCount} / {MAX_PRODUCT_DETAILS} details added
                {isDetailLimitHit && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-red-500">
                    <Lock className="w-3 h-3" /> Limit reached
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {Array.from({ length: MAX_PRODUCT_DETAILS }).map((_, i) => (
                    <div key={i} className={`h-1.5 w-10 rounded-full transition-colors ${i < detailCount ? 'bg-teal-500' : 'bg-gray-200'}`} />
                  ))}
                </div>
                <span className="text-xs text-gray-400">
                  {MAX_PRODUCT_DETAILS - detailCount} slot{MAX_PRODUCT_DETAILS - detailCount !== 1 ? 's' : ''} remaining
                </span>
              </div>
            </div>

            {isDetailLimitHit && (
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 text-sm text-amber-800">
                <Lock className="w-4 h-4 text-amber-500 shrink-0" />
                <span>You've reached the maximum of <strong>{MAX_PRODUCT_DETAILS} product details</strong>. Remove one to add another.</span>
              </div>
            )}

            {detailCount === 0 ? (
              <div className="border-2 border-dashed border-orange-200 rounded-xl p-10 text-center">
                <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No product details added yet.</p>
                <button type="button" onClick={addProductDetail}
                  className="mt-3 text-teal-600 text-sm font-medium hover:underline">
                  + Add your first detail
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {formData.productDetails.map((detail, idx) => (
                  <div key={idx} className="border border-orange-200 rounded-xl p-6 bg-orange-50 bg-opacity-40">
                    <div className="flex items-center justify-between mb-5">
                      <span className="text-sm font-bold text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-200">
                        Detail {idx + 1}
                      </span>
                      <button type="button" onClick={() => removeProductDetail(idx)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">
                            Title <span className="text-red-500">*</span>
                          </label>
                          <input type="text" value={detail.title} maxLength={100}
                            onChange={e => updateDetailField(idx, 'title', e.target.value)}
                            placeholder="e.g., Polaroid Style Glossy Photo Prints"
                            className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400" />
                          <div className="flex justify-end mt-1">
                            <span className="text-xs text-gray-400">{detail.title.length}/100</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">
                            Description <span className="text-red-500">*</span>
                          </label>
                          <textarea value={detail.content} maxLength={500} rows={6}
                            onChange={e => updateDetailField(idx, 'content', e.target.value)}
                            placeholder="Describe this feature in detail…"
                            className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none" />
                          <div className="flex justify-end mt-1">
                            <span className="text-xs text-gray-400">{detail.content.length}/500</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">
                          Image <span className="text-red-500">*</span>
                        </label>
                        {detail.preview ? (
                          <div className="relative group">
                            <img src={detail.preview} alt={`Detail ${idx + 1}`}
                              className="w-full h-56 object-cover rounded-xl border border-orange-100" />
                            <button type="button" onClick={() => removeDetailImage(idx)}
                              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                              <X className="w-3 h-3" />
                            </button>
                            <label className="absolute bottom-2 right-2 cursor-pointer">
                              <span className="px-3 py-1.5 bg-white bg-opacity-90 text-teal-700 text-xs font-medium rounded-lg border border-teal-200 hover:bg-teal-50 opacity-0 group-hover:opacity-100 inline-block transition-colors">
                                Replace
                              </span>
                              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                                onChange={e => { if (e.target.files[0]) handleDetailImageUpload(idx, e.target.files[0]); }} />
                            </label>
                          </div>
                        ) : (
                          <label className="block cursor-pointer">
                            <div className={`border-2 border-dashed rounded-xl p-8 text-center hover:border-teal-300 hover:bg-teal-50 transition-colors h-56 flex flex-col items-center justify-center
                              ${errors[`detail_image_${idx}`] ? 'border-red-300' : 'border-orange-200'}`}>
                              <Upload className="w-8 h-8 text-gray-300 mb-2" />
                              <p className="text-sm text-gray-500">
                                <span className="text-teal-600 font-medium">Click to upload</span> or drag & drop
                              </p>
                              <p className="text-xs text-gray-400 mt-1">PNG, JPG, WebP — max 5 MB</p>
                            </div>
                            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                              onChange={e => { if (e.target.files[0]) handleDetailImageUpload(idx, e.target.files[0]); }} />
                          </label>
                        )}
                        {errors[`detail_image_${idx}`] && (
                          <p className="text-red-500 text-sm mt-1">{errors[`detail_image_${idx}`]}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {!isDetailLimitHit && (
                  <button type="button" onClick={addProductDetail}
                    className="w-full py-3 border-2 border-dashed border-orange-200 rounded-xl text-sm text-gray-500 hover:border-teal-300 hover:text-teal-600 hover:bg-teal-50 transition-colors flex items-center justify-center gap-2">
                    <Plus className="w-4 h-4" /> Add Another Detail
                  </button>
                )}
              </div>
            )}
          </Section>

          {/* Footer actions */}
          <div className="flex justify-end gap-4 pt-8">
            <button type="button" onClick={() => navigate('/products')} disabled={isLoading}
              className="px-8 py-3 bg-white bg-opacity-80 text-gray-700 rounded-xl hover:bg-opacity-100 border border-gray-300 disabled:opacity-50 font-medium">
              Cancel
            </button>
            <button type="submit" disabled={isLoading}
              className="flex items-center gap-2 px-8 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:opacity-50 font-medium">
              {isLoading
                ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />{isEditMode ? 'Updating Product…' : 'Saving Product…'}</>
                : <><Save className="w-5 h-5" />{isEditMode ? 'Update Product' : 'Save Product'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductPage;

// ── Small reusable sub-components ─────────────────────────────────────────────

const Section = ({ icon, title, children, headerRight }) => (
  <div className="bg-white bg-opacity-80 rounded-xl border border-orange-200 p-8">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        {icon}
        <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
      </div>
      {headerRight}
    </div>
    {children}
  </div>
);

const Label = ({ children, required }) => (
  <label className="block text-sm font-semibold text-gray-800 mb-3">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const FieldError = ({ msg }) =>
  msg ? <p className="text-red-500 text-sm mt-1">{msg}</p> : null;

const DimRow = ({ label, valName, unitName, formData, onChange, error, units = ['cm', 'inches', 'mm'] }) => (
  <div className="grid grid-cols-2 gap-4">
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-2">{label}</label>
      <input type="number" name={valName} value={formData[valName]} onChange={onChange}
        placeholder="0.0" min="0" step="0.1"
        className="w-full px-4 py-2 bg-white border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-2">Unit</label>
      <select name={unitName} value={formData[unitName]} onChange={onChange}
        className="w-full px-4 py-2 bg-white border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400">
        {units.map(u => <option key={u} value={u}>{u}</option>)}
      </select>
    </div>
  </div>
);
